// V4.1.4: KumbaraItem Component
import React, { useState } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { KUMBARA_DURUMLARI } from '../constants';
import { styles } from '../constants/styles';
// V4.2.2: Toast notification
import { useToast } from './Toast';
import { alertToToast } from '../utils/toast';

const KumbaraItem = ({ kumbara, kumbaralar, setKumbaralar, teslimEdilenler, setTeslimEdilenler, siraNo }) => {
  // V4.2.2: Toast hook
  const toast = useToast();
  
  const [acik, setAcik] = useState(false);
  const [durum, setDurum] = useState(kumbara.durum);
  // V5.10: Teslim et modal state
  const [teslimModalAcik, setTeslimModalAcik] = useState(false);
  const [teslimSecenek, setTeslimSecenek] = useState('ayniIdIleDevam'); // V5.10: Varsayılan seçenek

  const guncelle = async () => {
    try {
      // V4.9: Token'ın hazır olduğundan emin ol (sadece bu işlem için)
      const { auth } = await import('../utils/firebase');
      const user = auth.currentUser;
      if (!user) {
        console.error('❌ auth.currentUser null!');
        alertToToast(toast, '❌ Oturum hatası: Lütfen çıkış yapıp tekrar giriş yapın.');
        return;
      }
      
      try {
        // Önce token'ı force refresh et
        await user.getIdToken(true); // Firestore SDK'nın token'ı yenilemesi için
        // Sonra claims'i al
        const tokenResult = await user.getIdTokenResult(true);
        console.log('✅ Token refresh tamamlandı (Kumbara güncelleme)');
        console.log('🔍 Custom claims kontrolü:', {
          rol: tokenResult.claims.rol,
          subeId: tokenResult.claims.subeId,
          kullaniciId: tokenResult.claims.kullaniciId
        });
        
        // Custom claims yüklenmemişse bekle ve tekrar dene (3 kez dene)
        let retryCount = 0;
        let finalTokenResult = tokenResult;
        while (!finalTokenResult.claims.rol && retryCount < 3) {
          console.warn(`⚠️ Custom claims henüz yüklenmemiş (deneme ${retryCount + 1}/3), 1000ms bekleniyor...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          // Tekrar token refresh et
          await user.getIdToken(true);
          finalTokenResult = await user.getIdTokenResult(true);
          console.log(`🔍 Token kontrolü (${retryCount + 1}/3):`, {
            rol: finalTokenResult.claims.rol,
            subeId: finalTokenResult.claims.subeId
          });
          retryCount++;
        }
        
        if (!finalTokenResult.claims.rol) {
          console.error('❌ Custom claims yüklenemedi! Token:', finalTokenResult);
          alertToToast(toast, '❌ Rol bilgisi yüklenemedi! Lütfen çıkış yapıp tekrar giriş yapın.');
          return;
        }
        
        // Firestore SDK'nın token'ı kullanması için bekleme
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (tokenError) {
        console.error('❌ Token refresh hatası:', tokenError);
        alertToToast(toast, '❌ Token hatası: Lütfen sayfayı yenileyin (F5).');
        return;
      }

      const kumbaraDoc = doc(db, 'kumbaralar', kumbara.firestoreId);
      await updateDoc(kumbaraDoc, { durum, sonZiyaret: new Date() });
      console.log('✅ Kumbara durumu güncellendi:', kumbara.id, '→', durum);
      setAcik(false);
      alertToToast(toast, '✅ Durum güncellendi! Liste otomatik güncellenecek.');
    } catch (error) {
      console.error('❌ Kumbara durum güncelleme hatası:', error);
      console.error('❌ Hata detayı:', error.code, error.message);
      if (error.code === 'permission-denied') {
        alertToToast(toast, '❌ İzin hatası: Lütfen sayfayı yenileyin (F5) ve tekrar deneyin.');
      } else {
        alertToToast(toast, '❌ Hata: ' + error.message);
      }
    }
  };

  // V5.10: Teslim et modalını aç
  const teslimEtModalAc = () => {
    setTeslimModalAcik(true);
    setTeslimSecenek('ayniIdIleDevam'); // Varsayılan seçenek
  };

  // V5.10: Versiyon hesaplama fonksiyonu
  const versiyonHesapla = (kumbaraId) => {
    // V5.10.1: Eğer kumbara zaten versiyonlu ID'ye sahipse (055-1), orijinal ID'yi (055) bul
    const orijinalId = kumbaraId.includes('-') ? kumbaraId.split('-')[0] : kumbaraId;
    
    // V5.10.4: Tüm versiyonlu ID'leri topla (hem teslimEdilenler hem kumbaralar hem de acilmisKumbaralar)
    const tumVersiyonluIdler = new Set();
    
    // Teslim edilenlerden versiyonlu ID'leri topla
    teslimEdilenler.forEach(t => {
      if (t.ayniIdIleDevam && t.orijinalId === orijinalId && t.id && t.id.includes('-')) {
        tumVersiyonluIdler.add(t.id);
      }
    });
    
    // Kumbaralar collection'ından versiyonlu ID'leri topla
    kumbaralar.forEach(k => {
      if (k.id.includes('-')) {
        const kOrijinalId = k.id.split('-')[0];
        if (kOrijinalId === orijinalId) {
          tumVersiyonluIdler.add(k.id);
        }
      }
    });
    
    // Versiyon numaralarını çıkar ve en yüksek versiyonu bul
    let enYuksekVersiyon = 0;
    tumVersiyonluIdler.forEach(versiyonluId => {
      const versiyonMatch = versiyonluId.match(/^(\d+)-(\d+)$/);
      if (versiyonMatch && versiyonMatch[1] === orijinalId) {
        const versiyonNum = parseInt(versiyonMatch[2], 10);
        if (versiyonNum > enYuksekVersiyon) {
          enYuksekVersiyon = versiyonNum;
        }
      }
    });
    
    // Yeni versiyon = en yüksek versiyon + 1
    const yeniVersiyon = enYuksekVersiyon + 1;
    
    return {
      versiyonluId: `${orijinalId}-${yeniVersiyon}`,
      versiyon: yeniVersiyon,
      orijinalId: orijinalId
    };
  };

  // V5.10: Teslim et işlemi
  const teslimEt = async () => {
    if (!teslimSecenek) {
      alertToToast(toast, '❌ Lütfen bir seçenek seçin!');
      return;
    }

    // V4.5: Token'ın hazır olduğundan emin ol (custom claims için)
    try {
      const { auth } = await import('../utils/firebase');
      const user = auth.currentUser;
      if (user) {
        try {
          await user.getIdToken(true); // Force refresh - custom claims yüklensin
        } catch (tokenError) {
          console.warn('Token refresh hatası (devam ediliyor):', tokenError);
        }
      }
    } catch (importError) {
      console.warn('Firebase import hatası (devam ediliyor):', importError);
    }

    try {
      // SEÇENEK 1: Aynı ID ile devam et
      if (teslimSecenek === 'ayniIdIleDevam') {
        const { versiyonluId, versiyon, orijinalId } = versiyonHesapla(kumbara.id);
        
        // V5.10.5: Teslim edilenlere ekle - MEVCUT ID'yi kullan (050, 050-1, 050-2), versiyonlu ID değil
        await addDoc(collection(db, 'teslimEdilenler'), {
          ...kumbara,
          id: kumbara.id, // V5.10.5: Mevcut ID (050, 050-1, 050-2) - teslim edilenler listesinde görünecek
          versiyonluId: versiyonluId, // Versiyonlu ID (050-1, 050-2, 050-3) - referans için
          orijinalId: orijinalId, // Orijinal ID (050) - referans için
          ayniIdIleDevam: true, // İşaret
          acildiAncakSayilmadi: true, // Durum
          teslimTarihi: new Date(),
          versiyon: versiyon
        });
        
        // Kumbara durumunu ve ID'sini güncelle (Boş olarak kalır, versiyonlu ID ile)
        const kumbaraDoc = doc(db, 'kumbaralar', kumbara.firestoreId);
        const yeniArsiv = [...(kumbara.arsiv || []), versiyonluId];
        
        await updateDoc(kumbaraDoc, {
          id: versiyonluId, // V5.10.1: ID'yi versiyonlu ID'ye güncelle (055 → 055-1)
          durum: 'Boş', // Dolu → Boş
          sonZiyaret: new Date(),
          arsiv: yeniArsiv // Versiyonlu ID arşive ekle
        });
        
        alertToToast(toast, `✅ Teslim edildi!\n\nKumbara ID: ${versiyonluId}\nDurum: Açıldı ancak sayılmadı\n\nKumbarayı açarak içindeki parayı boşaltıp tekrar boş olarak geri bıraktınız. Aldığınız miktarı daha sonra sayılacak ve işlem yapılacak şekilde muhafaza edin.`);
        setTeslimModalAcik(false);
        setAcik(false);
        return;
      }

      // SEÇENEK 2: Yeni boş kumbara bırak
      if (teslimSecenek === 'yeniKumbara') {
      // V4.5: Token'ın hazır olduğundan emin ol (custom claims için)
      try {
        const { auth } = await import('../utils/firebase');
        const user = auth.currentUser;
        if (user) {
          try {
            await user.getIdToken(true); // Force refresh - custom claims yüklensin
          } catch (tokenError) {
            console.warn('Token refresh hatası (devam ediliyor):', tokenError);
          }
        }
      } catch (importError) {
        console.warn('Firebase import hatası (devam ediliyor):', importError);
      }
        // Yeni kumbara ID'si sor
        const yeniID = window.prompt(`🆕 YENİ KUMBARA ID GİRİN:\n\nEski Kumbara: ${kumbara.id}\nFirma: ${kumbara.firmaAdi}\n\nYeni Kumbara ID:`);
        
        if (!yeniID || yeniID.trim() === '') {
          alertToToast(toast, '❌ Yeni kumbara ID girmediniz! İşlem iptal edildi.');
          setTeslimModalAcik(false);
          return;
        }
        
        // Teslim edilenlere ekle
        // V5.10.16: Yeni kumbara bırakıldı işareti ekle
        await addDoc(collection(db, 'teslimEdilenler'), {
          ...kumbara, 
          teslimTarihi: new Date(),
          yeniKumbaraBirakildi: true // V5.10.16: Yeni kumbara bırakıldı işareti
        });
        
        // Yeni kumbara bırak - eski ID'yi arşive ekle
        const kumbaraDoc = doc(db, 'kumbaralar', kumbara.firestoreId);
        const yeniArsiv = [...(kumbara.arsiv || []), kumbara.id];
        
        await updateDoc(kumbaraDoc, { 
          id: yeniID.trim(),
          durum: 'Boş', 
          sonZiyaret: new Date(),
          arsiv: yeniArsiv
        });
        
        alertToToast(toast, `✅ Teslim edildi!\n\nEski: ${kumbara.id} → Arşiv\nYeni: ${yeniID.trim()} → Boş kumbara bırakıldı`);
        setTeslimModalAcik(false);
        setAcik(false);
        return;
      }

      // SEÇENEK 3: Bu kumbara işletmeden çıkarılsın
      if (teslimSecenek === 'isletmedenCikar') {
        // V5.10.16: İşletmeden çıkarıldı işareti ekle
        await addDoc(collection(db, 'teslimEdilenler'), {
          ...kumbara, 
          teslimTarihi: new Date(),
          isletmedenCikarildi: true // V5.10.16: İşletmeden çıkarıldı işareti
        });
        await deleteDoc(doc(db, 'kumbaralar', kumbara.firestoreId));
        alertToToast(toast, '✅ Teslim edildi! Konum listeden çıkarıldı.');
        setTeslimModalAcik(false);
        setAcik(false);
        return;
      }
    } catch (error) {
      console.error('❌ Kumbara teslim hatası:', error);
      if (error.code === 'permission-denied') {
        alertToToast(toast, '❌ İzin hatası: Lütfen sayfayı yenileyin (F5) ve tekrar deneyin.');
      } else {
        alertToToast(toast, '❌ Hata: ' + error.message);
      }
      setTeslimModalAcik(false);
    }
  };

  return (
    <div style={{border: '1px solid #e2e8f0', borderRadius: '10px', padding: '15px', marginBottom: '15px'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
        <div style={{flex: 1}}>
          {siraNo && <p style={{fontSize: '12px', color: '#4299e1', marginBottom: '3px', fontWeight: 'bold'}}>#{String(siraNo).padStart(2, '0')}</p>}
          <p style={{fontSize: '11px', color: '#718096', marginBottom: '3px', textTransform: 'uppercase'}}>{kumbara.tip}</p>
          <p style={{fontSize: '10px', color: '#a0aec0', marginBottom: '3px'}}>ID: {kumbara.id}</p>
          <p style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '5px'}}>{kumbara.firmaAdi}</p>
          <p style={{fontSize: '15px', color: '#4a5568', marginBottom: '8px'}}>📍 {kumbara.adres}</p>
          <span style={{...styles.badge, background: kumbara.durum === 'Dolu' ? '#c6f6d5' : '#e2e8f0', color: kumbara.durum === 'Dolu' ? '#22543d' : '#2d3748'}}>
            {kumbara.durum}
          </span>
        </div>
        <button onClick={() => setAcik(!acik)} style={{...styles.btnPrimary, padding: '8px 16px'}}>
          {acik ? '❌' : '⚙️'} İşlem
        </button>
      </div>
      {acik && (
        <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e2e8f0'}}>
          <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <label style={{display: 'block', fontWeight: 'bold', fontSize: '14px'}}>Durum:</label>
            <select value={durum} onChange={(e) => setDurum(e.target.value)} style={{...styles.input, width: '100%', padding: '12px'}}>
              {KUMBARA_DURUMLARI.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <button onClick={guncelle} style={{...styles.btnSuccess, width: '100%', fontSize: '16px', fontWeight: 'bold', padding: '12px'}}>✅ Durumu Güncelle</button>
            {kumbara.durum === 'Dolu' && <button onClick={teslimEtModalAc} style={{...styles.btnSuccess, width: '100%', fontSize: '16px', fontWeight: 'bold', padding: '12px'}}>📦 TESLİM ET</button>}
          </div>
        </div>
      )}
      {kumbara.durum === 'Dolu' && !acik && (
        <button onClick={teslimEtModalAc} style={{...styles.btnSuccess, width: '100%', fontSize: '16px', fontWeight: 'bold', padding: '12px', marginTop: '10px'}}>
          TESLİM ET
        </button>
      )}
      
      {/* V5.10: Teslim Et Modal */}
      {teslimModalAcik && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '25px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h3 style={{margin: 0, fontSize: '20px', fontWeight: 'bold'}}>📦 KUMBARA TESLİM EDİLECEK</h3>
              <button onClick={() => setTeslimModalAcik(false)} style={{
                background: '#e53e3e',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                padding: '5px 10px',
                cursor: 'pointer',
                fontSize: '18px'
              }}>✕</button>
            </div>
            
            <div style={{marginBottom: '15px', padding: '15px', background: '#f7fafc', borderRadius: '10px'}}>
              <p style={{margin: 0, fontSize: '14px', color: '#4a5568'}}><strong>Kumbara ID:</strong> {kumbara.id}</p>
              <p style={{margin: '5px 0 0 0', fontSize: '14px', color: '#4a5568'}}><strong>Firma:</strong> {kumbara.firmaAdi}</p>
            </div>
            
            <div style={{marginBottom: '20px'}}>
              <label style={{display: 'block', marginBottom: '15px', cursor: 'pointer', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', background: teslimSecenek === 'ayniIdIleDevam' ? '#e6fffa' : 'white'}}>
                <div style={{display: 'flex', alignItems: 'start', gap: '10px'}}>
                  <input
                    type="radio"
                    name="teslimSecenek"
                    value="ayniIdIleDevam"
                    checked={teslimSecenek === 'ayniIdIleDevam'}
                    onChange={(e) => setTeslimSecenek(e.target.value)}
                    style={{marginTop: '3px'}}
                  />
                  <div style={{flex: 1}}>
                    <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '5px', color: '#2d3748'}}>
                      🔄 Aynı kumbara ID ile devam et
                    </div>
                    <div style={{fontSize: '13px', color: '#718096', marginTop: '5px', padding: '10px', background: '#fff5f5', borderRadius: '5px', border: '1px solid #fed7d7'}}>
                      <strong>⚠️ Dikkat!</strong> Kumbarayı açarak içindeki parayı boşaltıp tekrar boş olarak geri bırakın. Aldığınız miktarı daha sonra sayılacak ve işlem yapılacak şekilde muhafaza edin.
                    </div>
                  </div>
                </div>
              </label>
              
              <label style={{display: 'block', marginBottom: '15px', cursor: 'pointer', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', background: teslimSecenek === 'yeniKumbara' ? '#e6fffa' : 'white'}}>
                <div style={{display: 'flex', alignItems: 'start', gap: '10px'}}>
                  <input
                    type="radio"
                    name="teslimSecenek"
                    value="yeniKumbara"
                    checked={teslimSecenek === 'yeniKumbara'}
                    onChange={(e) => setTeslimSecenek(e.target.value)}
                    style={{marginTop: '3px'}}
                  />
                  <div style={{flex: 1}}>
                    <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '5px', color: '#2d3748'}}>
                      🆕 Yeni boş kumbara bırak
                    </div>
                    <div style={{fontSize: '13px', color: '#718096'}}>
                      Bu konuma yeni bir boş kumbara bırakılacak (yeni ID ile).
                    </div>
                  </div>
                </div>
              </label>
              
              <label style={{display: 'block', marginBottom: '15px', cursor: 'pointer', padding: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', background: teslimSecenek === 'isletmedenCikar' ? '#e6fffa' : 'white'}}>
                <div style={{display: 'flex', alignItems: 'start', gap: '10px'}}>
                  <input
                    type="radio"
                    name="teslimSecenek"
                    value="isletmedenCikar"
                    checked={teslimSecenek === 'isletmedenCikar'}
                    onChange={(e) => setTeslimSecenek(e.target.value)}
                    style={{marginTop: '3px'}}
                  />
                  <div style={{flex: 1}}>
                    <div style={{fontWeight: 'bold', fontSize: '16px', marginBottom: '5px', color: '#2d3748'}}>
                      🗑️ Bu kumbara işletmeden çıkarılsın
                    </div>
                    <div style={{fontSize: '13px', color: '#718096'}}>
                      Kumbara teslim edilecek ve bu konum listeden çıkarılacak.
                    </div>
                  </div>
                </div>
              </label>
            </div>
            
            <div style={{display: 'flex', gap: '10px'}}>
              <button onClick={() => setTeslimModalAcik(false)} style={{
                ...styles.btnDanger,
                flex: 1,
                padding: '12px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>İptal</button>
              <button onClick={teslimEt} style={{
                ...styles.btnSuccess,
                flex: 1,
                padding: '12px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>✅ Onayla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// V4.4.1: React.memo ile performans optimizasyonu
export default React.memo(KumbaraItem);

